import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuthUnified } from '@/hooks/shared/useAuthUnified';
import { LogoWithDomainSwitcher } from '@/shared/components/layout/LogoWithDomainSwitcher';
import { useSystemUsers } from '@/shared/hooks/useSystemUsers';
import { useAuthStore } from '@/shared/stores/authStore';
import { useProgramsStore } from '@/shared/stores/programsStore';
import { useAllTeamsGlobal } from '../../hooks/useAllTeamsGlobal';
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  X,
  FileSpreadsheet,
  FileText,
  PanelsTopLeft,
  Users,
  UserPlus,
  Star,
  Shield
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<any>;
  shortName?: string;
  expandable?: boolean;
}

/**
 * WorkflowSidebar component - Navigation sidebar for workflow domain
 * Responsive design with mobile support
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { hasGroupRouteAccess } = useAuthUnified();
  const { effectiveUser } = useAuthStore();
  const { users } = useSystemUsers();
  const location = useLocation();
  const navigate = useNavigate();

  const [isTeamWorkflowExpanded, setIsTeamWorkflowExpanded] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState<'team-workflow' | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const teamWorkflowButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load programs and ALL teams for starred teams feature
  // Each hook loads on mount with built-in deduplication via programsStore
  const loadPrograms = useProgramsStore(s => s.loadPrograms);
  useEffect(() => { loadPrograms(); }, [loadPrograms]);
  const { allTeamsGlobal } = useAllTeamsGlobal();

  // Get current user's starred teams
  const currentUser = useMemo(() => {
    if (!effectiveUser?.employee_id) return null;
    return users.find(u => u.employee_id === effectiveUser.employee_id);
  }, [users, effectiveUser]);

  const starredTeamIds = useMemo(() =>
    currentUser?.starred_teams || [],
    [currentUser]
  );

  // Filter allTeamsGlobal to only starred ones - these are ALL starred teams across all programs
  const starredTeams = useMemo(() => {
    if (!starredTeamIds.length) return [];
    return allTeamsGlobal.filter(team => starredTeamIds.includes(team.team_id));
  }, [allTeamsGlobal, starredTeamIds]);

  // Update dropdown position when hovering
  const updateDropdownPosition = (buttonRef: React.RefObject<HTMLButtonElement | null>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top,
        left: rect.right + 2
      });
    }
  };

  // Clear any pending close timeout
  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Schedule dropdown close with delay
  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredDropdown(null);
    }, 150);
  };

  // Auto-expand sections when on their routes
  useEffect(() => {
    if (location.pathname.startsWith('/workflow/team-workflow')) {
      setIsTeamWorkflowExpanded(true);
    }
  }, [location.pathname]);

  const handleTeamClick = (team: { team_id: string; program_id: string }) => {
    // Extract current tab path if we're already in a team workflow view
    const getCurrentTabPath = (): string => {
      const match = location.pathname.match(/\/workflow\/team-workflow\/program\/[^/]+\/team\/[^/]+$/);
      return match ? '' : '';
    };

    const tabSuffix = getCurrentTabPath();
    
    // Navigate to team while preserving any current context
    navigate(`/workflow/team-workflow/program/${team.program_id}/team/${team.team_id}${tabSuffix}`);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  // Navigation items for workflow domain
  const getNavigationItems = (): NavigationItem[] => {

    // Workflow-specific navigation items
    const workflowItems: NavigationItem[] = [
      {
        name: 'Workflow Summary',
        href: '/workflow/summary',
        icon: FileText,
        shortName: 'Summary'
      },
      {
        name: 'Team Planning',
        href: '/workflow/team-planning',
        icon: FileSpreadsheet,
        shortName: 'Planning'
      },
      {
        name: 'Team Workflow',
        href: '/workflow/team-workflow',
        icon: PanelsTopLeft,
        shortName: 'Workflow',
        expandable: starredTeams.length > 0
      },
      {
        name: 'Capacity',
        href: '/workflow/capacity',
        icon: Users,
        shortName: 'Capacity'
      },
      {
        name: 'Hiring Capacity',
        href: '/workflow/hiring-capacity',
        icon: UserPlus,
        shortName: 'Hiring'
      },
      {
        name: 'Admin',
        href: '/workflow/admin',
        icon: Shield,
        shortName: 'Admin'
      },
    ];

    const items = workflowItems.filter(item => hasGroupRouteAccess(item.href));

    return items;
  };

  const navigationItems = getNavigationItems();

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Floating Container */}
      <div
        className={`fixed z-50 bg-white transform transition-all duration-300 ease-in-out
          lg:left-4 lg:top-4 lg:bottom-4 lg:rounded-2xl lg:shadow-2xl lg:translate-x-0
          inset-y-0 left-0 shadow-lg
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-72'}
        `}
      >
        {/* Collapse toggle button - Desktop only, positioned outside overflow container */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex absolute -right-3 top-[68px] z-[60] w-6 h-6 items-center justify-center bg-white rounded-full border border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300 hover:scale-110 transition-all cursor-pointer"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
        >
          {isCollapsed ? (
            <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
          )}
        </button>

        <div className="flex flex-col h-full lg:rounded-2xl overflow-hidden">
          {/* Sidebar header - Mobile close button only */}
          <div className="flex items-center justify-end p-4 border-b border-gray-200 lg:hidden">
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Logo with integrated Domain Switcher */}
          <LogoWithDomainSwitcher isCollapsed={isCollapsed} />

          {/* Navigation */}
          <nav className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              const isTeamWorkflow = item.name === 'Team Workflow';
              
              return (
                <div key={item.name}>
                  <div className="relative">
                    {/* Team Workflow button - toggles expansion when starred teams exist */}
                    {isTeamWorkflow && (item as any).expandable ? (
                      <div
                        className="relative"
                        onMouseEnter={() => {
                          if (isCollapsed) {
                            clearCloseTimeout();
                            updateDropdownPosition(teamWorkflowButtonRef);
                            setHoveredDropdown('team-workflow');
                          }
                        }}
                        onMouseLeave={() => isCollapsed && scheduleClose()}
                      >
                        <button
                          ref={teamWorkflowButtonRef}
                          onClick={() => !isCollapsed && setIsTeamWorkflowExpanded(!isTeamWorkflowExpanded)}
                          className={`w-full group flex items-center text-sm font-medium rounded-lg transition-all ${
                            isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                          } ${
                            isActive
                              ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                              : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                          }`}
                          title={item.name}
                        >
                          <Icon
                            className={`h-5 w-5 transition-colors ${
                              isCollapsed ? '' : 'mr-3'
                            } ${
                              isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                            }`}
                          />
                          {!isCollapsed && (
                            <>
                              <span className="flex-1 text-left">
                                {item.shortName || item.name}
                              </span>
                              {isTeamWorkflowExpanded ? (
                                <ChevronDown className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                              ) : (
                                <ChevronRight className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                              )}
                            </>
                          )}
                        </button>
                        
                        {/* Collapsed dropdown menu for Team Workflow */}
                        {isCollapsed && hoveredDropdown === 'team-workflow' && (
                          <div
                            className="fixed w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[5000]"
                            style={{
                              left: `${dropdownPosition.left}px`,
                              top: `${dropdownPosition.top}px`
                            }}
                            onMouseEnter={() => clearCloseTimeout()}
                            onMouseLeave={() => scheduleClose()}
                          >
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Team Workflow
                            </div>
                            <NavLink
                              to="/workflow/team-workflow"
                              onClick={() => {
                                setHoveredDropdown(null);
                                if (window.innerWidth < 1024) {
                                  onClose();
                                }
                              }}
                              className={`flex items-center px-3 py-2 text-sm transition-colors ${
                                location.pathname === '/workflow/team-workflow'
                                  ? 'bg-purple-50 text-purple-900 font-medium'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <PanelsTopLeft className={`mr-3 h-4 w-4 ${
                                location.pathname === '/workflow/team-workflow' ? 'text-purple-600' : 'text-gray-400'
                              }`} />
                              All Workflows
                            </NavLink>
                            {starredTeams.length > 0 && (
                              <>
                                <div className="px-3 py-1 mt-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Starred
                                </div>
                                {starredTeams.map((team) => {
                                  const teamPath = `/workflow/team-workflow/program/${team.program_id}/team/${team.team_id}`;
                                  const isTeamActive = location.pathname.startsWith(teamPath);
                                  
                                  return (
                                    <button
                                      key={team.team_id}
                                      onClick={() => {
                                        handleTeamClick(team);
                                        setHoveredDropdown(null);
                                      }}
                                      className={`w-full flex items-center px-3 py-2 text-sm transition-colors ${
                                        isTeamActive
                                          ? 'bg-purple-50 text-purple-900'
                                          : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                      title={team.team_name}
                                    >
                                      <Star className={`mr-3 h-3.5 w-3.5 flex-shrink-0 ${
                                        isTeamActive ? 'text-yellow-500 fill-yellow-500' : 'text-yellow-400 fill-yellow-400'
                                      }`} />
                                      <span className="truncate text-left">{team.team_name}</span>
                                    </button>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Regular NavLink for items without starred items */
                      <NavLink
                        to={item.href}
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                        className={`group flex items-center text-sm font-medium rounded-lg transition-all ${
                          isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                        } ${
                          isActive
                            ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                            : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                        }`}
                        title={item.name}
                      >
                        <Icon
                          className={`h-5 w-5 transition-colors ${
                            isCollapsed ? '' : 'mr-3'
                          } ${
                            isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                          }`}
                        />
                        {!isCollapsed && (
                          <span className="flex-1">
                            {item.shortName || item.name}
                          </span>
                        )}
                      </NavLink>
                    )}
                  </div>

                  {/* Starred Teams Submenu with "All Workflows" option */}
                  {!isCollapsed && isTeamWorkflow && isTeamWorkflowExpanded && starredTeams.length > 0 && (
                    <div className="ml-8 mt-1 space-y-1">
                      {/* All Workflows option */}
                      <NavLink
                        to="/workflow/team-workflow"
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            onClose();
                          }
                        }}
                        className={`group flex items-center px-3 py-2 text-sm rounded-lg transition-all ${
                          location.pathname === '/workflow/team-workflow'
                            ? 'bg-purple-100 text-purple-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        title="All Workflows"
                      >
                        <PanelsTopLeft
                          className={`mr-2 h-3.5 w-3.5 ${
                            location.pathname === '/workflow/team-workflow' ? 'text-purple-700' : 'text-gray-400'
                          }`}
                        />
                        <span className="flex-1 text-left">
                          All Workflows
                        </span>
                      </NavLink>
                      
                      {/* Starred teams */}
                      {starredTeams.map((team) => {
                        const teamPath = `/workflow/team-workflow/program/${team.program_id}/team/${team.team_id}`;
                        const isTeamActive = location.pathname.startsWith(teamPath);
                        
                        return (
                          <button
                            key={team.team_id}
                            onClick={() => handleTeamClick(team)}
                            className={`w-full group flex items-center px-3 py-2 text-sm rounded-lg transition-all ${
                              isTeamActive
                                ? 'bg-purple-100 text-purple-900'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            title={team.team_name}
                          >
                            <Star
                              className={`mr-2 h-3.5 w-3.5 ${
                                isTeamActive ? 'text-yellow-500 fill-yellow-500' : 'text-yellow-400 fill-yellow-400'
                              }`}
                            />
                            <span className="flex-1 text-left truncate">
                              {team.team_name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

        </div>
      </div>
    </>
  );
};

export default Sidebar;
